import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  GitBranch, Ship, Anchor, DollarSign, Users
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';

export default function BenchmarkingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get('/executive/benchmarking');
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const routeChartData = (data?.routes || []).slice(0, 8).map(r => ({
    route: (r.route || '').length > 22 ? (r.route || '').substring(0, 20) + '...' : r.route,
    total: parseFloat(r.total_cost || 0),
    avg: parseFloat(r.avg_cost || 0),
  }));

  const vendorChartData = (data?.vendors || []).slice(0, 8).map(v => ({
    vendor: (v.vendor_name || '').length > 15 ? (v.vendor_name || '').substring(0, 13) + '...' : v.vendor_name,
    total: parseFloat(v.total_amount || 0),
    disputes: v.dispute_count || 0,
  }));

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="benchmarking-loading">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="benchmarking-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Portfolio Benchmarking</h1>
        <p className="text-sm text-slate-500 mt-1">Cross-portfolio performance scorecards — route, vendor, terminal & vessel</p>
      </div>

      <Tabs defaultValue="routes">
        <TabsList className="rounded-sm h-9 bg-slate-100 p-0.5">
          <TabsTrigger value="routes" className="rounded-sm text-xs flex-1" data-testid="tab-routes">
            <GitBranch className="h-3 w-3 mr-1" />Routes ({data?.routes?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-sm text-xs flex-1" data-testid="tab-vendors">
            <Users className="h-3 w-3 mr-1" />Vendors ({data?.vendors?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="terminals" className="rounded-sm text-xs flex-1" data-testid="tab-terminals">
            <Anchor className="h-3 w-3 mr-1" />Terminals ({data?.terminals?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="vessels" className="rounded-sm text-xs flex-1" data-testid="tab-vessels-bench">
            <Ship className="h-3 w-3 mr-1" />Vessels ({data?.vessels?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Routes */}
        <TabsContent value="routes">
          <div className="space-y-4">
            {routeChartData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Total Cost by Route</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={routeChartData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="route" tick={{ fontSize: 9, fill: '#64748b' }} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}`} />
                        <Bar dataKey="total" fill="#0A2463" radius={[2, 2, 0, 0]} barSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-0">
                <table className="w-full text-sm" data-testid="routes-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Route</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Trades</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg Cost</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total Cost</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg Cargo (MT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.routes || []).map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-2 text-xs font-medium text-slate-800">{r.route}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{r.trade_count}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(r.avg_cost || 0).toLocaleString()}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right font-bold text-slate-900">${parseFloat(r.total_cost || 0).toLocaleString()}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(r.avg_cargo_mt || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vendors */}
        <TabsContent value="vendors">
          <div className="space-y-4">
            {vendorChartData.length > 0 && (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Total Invoiced by Vendor</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={vendorChartData} margin={{ left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="vendor" tick={{ fontSize: 10, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={v => `$${v.toLocaleString()}`} />
                        <Bar dataKey="total" fill="#0A2463" radius={[2, 2, 0, 0]} barSize={28} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-0">
                <table className="w-full text-sm" data-testid="vendors-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vendor</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Invoices</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg Amount</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Disputes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.vendors || []).map((v, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-2 text-xs font-medium text-slate-800">{v.vendor_name}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{v.invoice_count}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">${parseFloat(v.avg_amount || 0).toLocaleString()}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right font-bold text-slate-900">${parseFloat(v.total_amount || 0).toLocaleString()}</td>
                        <td className="px-5 py-2 text-right">
                          {v.dispute_count > 0 ? (
                            <Badge variant="outline" className="rounded-sm text-[9px] border-red-300 text-red-700 bg-red-50">{v.dispute_count}</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Terminals */}
        <TabsContent value="terminals">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              {(data?.terminals || []).length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-400">No terminal data yet</div>
              ) : (
                <table className="w-full text-sm" data-testid="terminals-bench-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Terminal</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Port Calls</th>
                      <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.terminals || []).map((t, i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-2 text-xs font-medium text-slate-800">{t.terminal_name}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{t.call_count}</td>
                        <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{t.avg_hours ? parseFloat(t.avg_hours).toFixed(1) : '—'}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vessels */}
        <TabsContent value="vessels">
          <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="vessels-bench-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">IMO</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Voyages</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg EEOI</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Avg CII</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total CO2e</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Total Fuel</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.vessels || []).map((v, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-5 py-2 text-xs font-medium text-slate-800">{v.name}</td>
                      <td className="px-5 py-2 text-xs font-mono text-slate-500">{v.imo}</td>
                      <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{v.voyage_count}</td>
                      <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.avg_eeoi || 0).toFixed(2)}</td>
                      <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.avg_cii || 0).toFixed(4)}</td>
                      <td className="px-5 py-2 text-xs font-mono text-right font-bold text-slate-900">{parseFloat(v.total_emissions || 0).toLocaleString()} t</td>
                      <td className="px-5 py-2 text-xs font-mono text-right text-slate-600">{parseFloat(v.total_fuel || 0).toLocaleString()} t</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
